import { useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { SEED_CONTRACTS, gm, op } from "../data/contractsSeed";
import { personById } from "../lib/orgHierarchy";

function fmtMoney(n: number) {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtPct(n: number) {
  return `${n.toFixed(1)}%`;
}

export function PortfolioPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const isDir = roleIds.includes("ROLE-DIRECTOR");
  const isVp = roleIds.includes("ROLE-VP");
  const can =
    moduleIds.includes("portfolio") ||
    isDir ||
    isVp ||
    roleIds.includes("ROLE-EXECUTIVE-BI-AUDITOR");

  // Demo attribution: Director Brooks = emp-dir-fc; VP = emp-vp-del
  const focusDirector = "emp-dir-fc";
  const focusVp = "emp-vp-del";

  const rows = useMemo(() => {
    if (isVp || (!isDir && roleIds.includes("ROLE-EXECUTIVE-BI-AUDITOR"))) {
      return SEED_CONTRACTS.filter((c) => c.vpId === focusVp);
    }
    if (isDir) {
      return SEED_CONTRACTS.filter((c) => c.directorId === focusDirector);
    }
    return SEED_CONTRACTS;
  }, [isDir, isVp, roleIds]);

  const totals = useMemo(() => {
    const acc = {
      revA: 0,
      revP: 0,
      dirA: 0,
      dirP: 0,
      burA: 0,
      burP: 0,
    };
    for (const c of rows) {
      acc.revA += c.revenueActual;
      acc.revP += c.revenueProj;
      acc.dirA += c.directCostActual;
      acc.dirP += c.directCostProj;
      acc.burA += c.burdenActual;
      acc.burP += c.burdenProj;
    }
    return acc;
  }, [rows]);

  const byDirector = useMemo(() => {
    if (!isVp) return [] as [string, typeof rows][];
    const map = new Map<string, typeof rows>();
    for (const c of rows) {
      const list = map.get(c.directorId) ?? [];
      list.push(c);
      map.set(c.directorId, list);
    }
    return [...map.entries()];
  }, [isVp, rows]);

  if (!can) return <Navigate to={homePath} replace />;

  const title = isVp ? "VP portfolio" : isDir ? "Director portfolio" : "Portfolio";
  const gmA = gm(totals.revA, totals.dirA);
  const gmP = gm(totals.revP, totals.dirP);
  const opA = op(totals.revA, totals.dirA, totals.burA);
  const opP = op(totals.revP, totals.dirP, totals.burP);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal">
          REL-021 · DEC-PORTFOLIO-001
        </p>
        <h1 className="mt-2 font-display text-3xl text-navy">{title}</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink-muted leading-relaxed">
          Actuals and projections for Gross Margin and Operating Profit ($ and %). Figures are
          provisional stubs until Finance depth lands — scoped by contract attribution.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        {[
          { label: "GM $ actual", value: fmtMoney(gmA.dollars) },
          { label: "GM % actual", value: fmtPct(gmA.pct) },
          { label: "OP $ actual", value: fmtMoney(opA.dollars) },
          { label: "OP % actual", value: fmtPct(opA.pct) },
          { label: "GM $ projected", value: fmtMoney(gmP.dollars) },
          { label: "GM % projected", value: fmtPct(gmP.pct) },
          { label: "OP $ projected", value: fmtMoney(opP.dollars) },
          { label: "OP % projected", value: fmtPct(opP.pct) },
        ].map((m) => (
          <div key={m.label} className="rounded-xl border border-navy-100 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-ink-subtle">{m.label}</div>
            <div className="mt-1 text-2xl font-semibold text-navy">{m.value}</div>
          </div>
        ))}
      </div>

      {isVp && byDirector.length > 0 && (
        <section className="mb-6 rounded-xl border border-navy-100 bg-white p-5">
          <h2 className="font-display text-lg text-navy">By Director</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {byDirector.map(([dirId, list]) => {
              const rev = list.reduce((s, c) => s + c.revenueActual, 0);
              const dir = list.reduce((s, c) => s + c.directCostActual, 0);
              const bur = list.reduce((s, c) => s + c.burdenActual, 0);
              const g = gm(rev, dir);
              const o = op(rev, dir, bur);
              return (
                <li
                  key={dirId}
                  className="flex flex-wrap justify-between gap-2 border-t border-navy-50 pt-2 first:border-0 first:pt-0"
                >
                  <span className="font-medium text-navy">
                    {personById(dirId)?.name ?? dirId}
                  </span>
                  <span className="text-ink-muted">
                    GM {fmtMoney(g.dollars)} ({fmtPct(g.pct)}) · OP {fmtMoney(o.dollars)} (
                    {fmtPct(o.pct)})
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-navy-100 bg-white overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-navy-50 text-xs uppercase tracking-wide text-ink-subtle">
            <tr>
              <th className="px-3 py-2">Contract</th>
              <th className="px-3 py-2">GM act / proj</th>
              <th className="px-3 py-2">OP act / proj</th>
              <th className="px-3 py-2">Attribution</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const ga = gm(c.revenueActual, c.directCostActual);
              const gp = gm(c.revenueProj, c.directCostProj);
              const oa = op(c.revenueActual, c.directCostActual, c.burdenActual);
              const op_ = op(c.revenueProj, c.directCostProj, c.burdenProj);
              return (
                <tr key={c.id} className="border-t border-navy-100 align-top">
                  <td className="px-3 py-2">
                    <Link to="/app/contracts" className="font-medium text-navy hover:text-brand-teal">
                      {c.title}
                    </Link>
                    <div className="font-mono text-xs text-ink-subtle">
                      {c.internalVvCcc}.{c.period}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-ink-muted">
                    {fmtMoney(ga.dollars)} ({fmtPct(ga.pct)})
                    <br />
                    <span className="text-xs">
                      proj {fmtMoney(gp.dollars)} ({fmtPct(gp.pct)})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-ink-muted">
                    {fmtMoney(oa.dollars)} ({fmtPct(oa.pct)})
                    <br />
                    <span className="text-xs">
                      proj {fmtMoney(op_.dollars)} ({fmtPct(op_.pct)})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-muted">
                    PM {personById(c.pmId)?.name}
                    <br />
                    Dir {personById(c.directorId)?.name}
                    {c.directorOverridden ? " *" : ""}
                    <br />
                    VP {personById(c.vpId)?.name}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
